import { World } from "./world";
import { KIND_UNIT, KIND_BUILDING } from "./constants";
import type { Sim } from "./sim";
import { applyCommands } from "./apply";
import { canPlace, nearestFreeSpot } from "./placement";
import { getFaction, getDef, type Faction } from "../data";

// Skirmish AI (GDD §9.5): a utility state machine driven by economy feedback and
// army-value ratios. It is deterministic (uses sim.rng, never the wall clock),
// so it reproduces identically in replays and the determinism test.

export type Difficulty = "easy" | "normal" | "hard";

interface Ids {
  extractor: string;
  solar: string;
  wind: string;
  converter: string;
  storage: string;
  factory: string;
  turret: string;
  constructor: string;
  army: string[]; // weighted army roster (defId repeated by desired ratio)
}

const VANTAGE_ARMY = ["v_blade", "v_blade", "v_warden", "v_warden", "v_warden", "v_lancer", "v_claw", "v_spike", "v_ray"];
const FERAL_ARMY = ["f_pup", "f_pup", "f_brute", "f_brute", "f_brute", "f_fist", "f_roller", "f_burst", "f_lobber", "f_tick"];

export class Skirmisher {
  readonly owner: number;
  readonly enemyOwner: number;
  readonly base: { x: number; y: number };
  readonly staging: { x: number; y: number };
  private ids: Ids;
  private decisionEvery: number;
  private armyThreshold: number;
  private attacking = false;

  constructor(owner: number, enemyOwner: number, faction: Faction, base: { x: number; y: number }, enemyBase: { x: number; y: number }, diff: Difficulty) {
    this.owner = owner;
    this.enemyOwner = enemyOwner;
    this.base = base;
    const f = getFaction(faction);
    this.ids = {
      extractor: prefix(f.id, "extractor"),
      solar: prefix(f.id, "solar"),
      wind: prefix(f.id, "wind"),
      converter: prefix(f.id, "converter"),
      storage: prefix(f.id, "storage"),
      factory: f.factoryId,
      turret: prefix(f.id, "turret"),
      constructor: faction === "vantage" ? "v_fabber" : "f_welder",
      army: faction === "vantage" ? VANTAGE_ARMY : FERAL_ARMY,
    };
    const dx = enemyBase.x - base.x;
    const dy = enemyBase.y - base.y;
    const m = Math.hypot(dx, dy) || 1;
    this.staging = { x: base.x + (dx / m) * 110, y: base.y + (dy / m) * 110 };
    this.decisionEvery = diff === "hard" ? 12 : diff === "normal" ? 20 : 32;
    this.armyThreshold = diff === "hard" ? 900 : diff === "normal" ? 1300 : 1800;
  }

  update(w: World, sim: Sim): void {
    if (w.tick % this.decisionEvery !== 0) return;

    // --- Gather state ---
    const builders: number[] = [];
    const idleBuilders: number[] = [];
    const army: number[] = [];
    const factories: number[] = [];
    let extractors = 0, solars = 0, winds = 0, converters = 0, storages = 0, turrets = 0;
    let energyProd = 0;
    let commander = -1;

    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.owner[e] !== this.owner) continue;
      const def = w.def(e);
      const built = !w.underConstruction(e);
      if (def.role === "commander") commander = e;
      if (def.buildPower > 0 && w.kind[e] === KIND_UNIT) {
        builders.push(e);
        if (w.orders[e].length === 0) idleBuilders.push(e);
      }
      if (def.role === "factory") factories.push(e);
      if (def.role === "extractor") extractors++;
      if (def.role === "solar") { solars++; if (built) energyProd += 20; }
      if (def.role === "wind") { winds++; if (built) energyProd += w.windCurrent; }
      if (def.role === "converter") converters++;
      if (def.role === "storage") storages++;
      if (def.role === "turret") turrets++;
      if ((def.weapon || def.role === "bomb") && w.kind[e] === KIND_UNIT && def.role !== "commander") {
        army.push(e);
      }
    }

    // --- Resume orphaned construction sites (builder died / was interrupted) ---
    // Any under-construction friendly building that no living builder is currently
    // working is finished by an idle qualified builder, so the AI never abandons a
    // half-built structure. Cheap: O(builders + entities) per decision tick.
    const attended = new Set<number>();
    for (const b of builders) {
      const o = w.orders[b][0];
      if (o?.kind === "build") attended.add(o.buildEnt);
      else if (o?.kind === "repair") attended.add(o.target);
    }
    const freeIdle: number[] = [];
    for (const b of idleBuilders) {
      let resumed = false;
      for (let e = 0; e < w.count; e++) {
        if (!w.alive[e] || w.owner[e] !== this.owner || w.kind[e] !== KIND_BUILDING) continue;
        if (!w.underConstruction(e) || attended.has(e)) continue;
        if (!(w.def(b).buildOptions?.includes(w.def(e).id) ?? false)) continue;
        applyCommands(w, sim, [{ t: "repair", ents: [b], target: e, queue: false }]);
        attended.add(e);
        resumed = true;
        break;
      }
      if (!resumed) freeIdle.push(b);
    }

    // --- Economy build orders for idle builders ---
    for (const b of freeIdle) {
      const task = this.chooseTask(w, { extractors, solars, factories: factories.length, converters, turrets, energyProd });
      if (!task) break;
      const pos = this.findSpot(w, sim, task);
      if (!pos) continue;
      applyCommands(w, sim, [{ t: "build", ents: [b], defId: task, x: pos.x, y: pos.y, queue: false }]);
      // Update running tallies so multiple idle builders don't stack the same thing.
      if (task === this.ids.extractor) extractors++;
      else if (task === this.ids.solar) { solars++; energyProd += 20; }
      else if (task === this.ids.factory) factories.push(b);
      else if (task === this.ids.converter) converters++;
      else if (task === this.ids.turret) turrets++;
    }

    // --- Factory production ---
    for (const f of factories) {
      if (w.underConstruction(f)) continue;
      // Keep rally at staging.
      applyCommands(w, sim, [{ t: "factoryRally", factory: f, x: this.staging.x, y: this.staging.y }]);
      if (w.factoryQueue[f].length >= 3) continue;
      const constructorCount = builders.length;
      let pick: string;
      if (constructorCount < 3 && sim.rng.next() < 0.5) {
        pick = this.ids.constructor;
      } else {
        pick = this.ids.army[sim.rng.int(0, this.ids.army.length - 1)];
      }
      applyCommands(w, sim, [{ t: "factoryQueue", factory: f, defId: pick, count: 1 }]);
    }

    // --- Army command ---
    this.commandArmy(w, sim, army, commander);
  }

  private chooseTask(
    w: World,
    s: { extractors: number; solars: number; factories: number; converters: number; turrets: number; energyProd: number },
  ): string | null {
    const hasFreeSpot = nearestFreeSpot(w, this.base.x, this.base.y, 500) >= 0;
    const energyNeed = 60 + s.extractors * 12 + s.factories * 40;

    if (s.factories === 0) {
      if (s.extractors < 1 && hasFreeSpot) return this.ids.extractor;
      if (s.energyProd < 30) return this.ids.solar;
      if (s.extractors < 2 && hasFreeSpot) return this.ids.extractor;
      return this.ids.factory; // factory as the 3rd/4th building, then snowball
    }
    if (hasFreeSpot && s.extractors < w.gmap.def.metalSpots.length) return this.ids.extractor;
    if (s.energyProd < energyNeed) return this.ids.solar;
    if (s.converters === 0 && s.energyProd > 220) return this.ids.converter;
    if (s.turrets < 2) return this.ids.turret;
    if (s.factories < 2) return this.ids.factory;
    return this.ids.solar; // spend surplus build power on more energy
  }

  private findSpot(w: World, sim: Sim, defId: string): { x: number; y: number } | null {
    if (getDef(defId).role === "extractor") {
      const s = nearestFreeSpot(w, this.base.x, this.base.y, 500);
      if (s < 0) return null;
      return { x: w.gmap.def.metalSpots[s].x, y: w.gmap.def.metalSpots[s].y };
    }
    // Ring search around base for a legal spot.
    for (let ring = 1; ring <= 6; ring++) {
      for (let a = 0; a < 8; a++) {
        const ang = (a / 8) * Math.PI * 2 + sim.rng.next() * 0.4;
        const r = 40 + ring * 26;
        const x = this.base.x + Math.cos(ang) * r;
        const y = this.base.y + Math.sin(ang) * r;
        if (canPlace(w, defId, x, y)) return { x, y };
      }
    }
    return null;
  }

  private commandArmy(w: World, sim: Sim, army: number[], commander: number): void {
    if (army.length === 0) return;

    // Retreat badly damaged units to base.
    for (const u of army) {
      if (w.health[u] / w.maxHealth[u] < 0.3) {
        const o = w.orders[u][0];
        if (!o || o.kind !== "move" || Math.hypot(o.x - this.base.x, o.y - this.base.y) > 30) {
          applyCommands(w, sim, [{ t: "move", ents: [u], x: this.base.x, y: this.base.y, queue: false, attackMove: false }]);
        }
      }
    }

    const myValue = this.armyValue(w, army);
    const target = this.pickTarget(w);
    const enemyDefense = target ? this.enemyStrengthNear(w, target.x, target.y, 260) : 0;

    if (!this.attacking) {
      // Idle army gathers at staging.
      for (const u of army) {
        if (w.orders[u].length === 0 && w.health[u] / w.maxHealth[u] >= 0.3) {
          applyCommands(w, sim, [{ t: "move", ents: [u], x: this.staging.x, y: this.staging.y, queue: false, attackMove: true }]);
        }
      }
      // Commit to attack when strong enough, willing to trade, or when the army
      // is simply overwhelming (breaks turtle standoffs; punishes an AFK player).
      const strongEnough =
        target != null &&
        army.length >= 6 &&
        (myValue > Math.max(this.armyThreshold, enemyDefense * 0.9) || myValue > this.armyThreshold * 2.2);
      if (strongEnough && target) {
        this.attacking = true;
        applyCommands(w, sim, [{ t: "move", ents: army.filter((u) => w.health[u] / w.maxHealth[u] >= 0.3), x: target.x, y: target.y, queue: false, attackMove: true }]);
      }
    } else {
      // Keep pressing the target; re-issue to refresh flow field toward it.
      if (target) {
        applyCommands(w, sim, [{ t: "move", ents: army.filter((u) => w.health[u] / w.maxHealth[u] >= 0.4), x: target.x, y: target.y, queue: false, attackMove: true }]);
      }
      // Break off if the push has been gutted.
      if (myValue < enemyDefense * 0.6 || army.length < 4) {
        this.attacking = false;
      }
    }

    // Keep the commander home and safe.
    if (commander >= 0) {
      const threat = this.enemyStrengthNear(w, w.posX[commander], w.posY[commander], 170);
      const home = Math.hypot(w.posX[commander] - this.base.x, w.posY[commander] - this.base.y);
      if (threat > 200 && home > 40 && w.orders[commander].length === 0) {
        applyCommands(w, sim, [{ t: "move", ents: [commander], x: this.base.x, y: this.base.y, queue: false, attackMove: false }]);
      }
    }
  }

  private armyValue(w: World, army: number[]): number {
    let v = 0;
    for (const u of army) v += w.def(u).metalCost * (w.health[u] / w.maxHealth[u]);
    return v;
  }

  private enemyStrengthNear(w: World, x: number, y: number, radius: number): number {
    let v = 0;
    const r2 = radius * radius;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.owner[e] !== this.enemyOwner) continue;
      const def = w.def(e);
      if (!def.weapon && def.role !== "bomb") continue;
      const dx = w.posX[e] - x;
      const dy = w.posY[e] - y;
      if (dx * dx + dy * dy <= r2) v += def.metalCost;
    }
    return v;
  }

  private pickTarget(w: World): { x: number; y: number } | null {
    // Prefer the enemy commander; otherwise the nearest enemy structure/unit.
    let commanderPos: { x: number; y: number } | null = null;
    let nearest: { x: number; y: number } | null = null;
    let nd = Infinity;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.owner[e] !== this.enemyOwner) continue;
      if (w.kind[e] !== KIND_UNIT && w.kind[e] !== KIND_BUILDING) continue;
      const def = w.def(e);
      if (def.role === "commander") commanderPos = { x: w.posX[e], y: w.posY[e] };
      const d = Math.hypot(w.posX[e] - this.staging.x, w.posY[e] - this.staging.y);
      if (d < nd) {
        nd = d;
        nearest = { x: w.posX[e], y: w.posY[e] };
      }
    }
    return commanderPos ?? nearest;
  }
}

function prefix(f: Faction, role: string): string {
  return `${f === "vantage" ? "v" : "f"}_${role}`;
}
